import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { RootState } from '../../../lib/store';
import { processSymbolUnSubscribesAction } from '../../../lib/store/common-actions';
import TradePriceCache from '../../../lib/store-util/TradePriceCache';
import { formatCurrency, getLimitedText } from '../../../lib/util/DataHelpers';
import { calculatePriceMovement, roundToCurrencyFormat } from '../../../lib/util/lastPriceChangeHelpers';
import { INVALID_VALUE, PriceMovement } from '../../../lib/util/types';

import removeIcon from '../../../assets/img/modify.svg';
import { useIsMobile } from '../../../hooks';
import { UI_ROUTES } from '../../../util/routes';
import ChartLine from '../../Chart/ChartLine/ChartLine';
import ClickableImage from '../../ClickableImage/ClickableImage';
import CompanyLogo from '../../CompanyLogo/CompanyLogo';
import { WatchListSortedStocks, WatchListStocks } from '../WatchList.types';

import styles from './WatchListTable.module.scss';

let priceMovement: PriceMovement;
const NAME_LENGTH_LIMIT = 28;

const WatchListRow = (props:{
  stock:WatchListStocks,
  tempDataStocks: WatchListSortedStocks[];
  updateTempDataStocks: (updatedStock: WatchListSortedStocks) => void;
  hasSwipeableRows?:boolean
  swipedRow?:string
  setSwipedRow?:React.Dispatch<React.SetStateAction<string>>
}):JSX.Element => {
  const {
    stock,
    tempDataStocks,
    updateTempDataStocks,
    hasSwipeableRows,
    swipedRow,
    setSwipedRow,
  } = props;

  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const dispatch = useDispatch();

  const [removeSymbol, setRemoveSymbol] = useState<string>('');

  const renderName = (name) => {
    if (!isMobile || name.length <= 15) return name;
    return name.slice(0, 11).concat('...');
  };

  const companyLogosAndNames = useSelector((state: RootState) => state.fundamental.companyLogoAndName);
  const userCorrelationId = useSelector((state: RootState) => state.crm.individualExtendedInfo?.user_correlation_id);
  const { logo, name, url } = companyLogosAndNames[stock?.symbol] ?? {};
  const displayName = name ?? stock.companyName ?? '';
  const {
    direction, change, changePercentWithSign, currentPrice,
  } = TradePriceCache.use(stock.symbol, 'WatchListTable');
  priceMovement = calculatePriceMovement(currentPrice, stock.symbol);

  const tempDataStock = {
    symbol: stock.symbol,
    currentPrice: roundToCurrencyFormat(currentPrice || 0),
    change: parseFloat(change || '0'),
    changePercent: parseFloat(changePercentWithSign || '0'),
    logoBase64: logo,
    companyName: displayName,
    url,
  };

  // Update tempDataStocks in the parent
  useEffect(() => {
    updateTempDataStocks(tempDataStock);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPrice, change, changePercentWithSign, logo, displayName, url]);

  useEffect(() => {
    const symbol = removeSymbol;
    setSwipedRow && setSwipedRow('');
    setRemoveSymbol('');

    dispatch(processSymbolUnSubscribesAction({
      symbolOrSymbols: symbol,
      isRemovingFromFavorites: true,
      group: 'watchlist',
      caller: 'SymbolDetailsHeader',
      userCorrelationId,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeSymbol]);

  let touchStartX = 0;
  let touchEndX = 0;

  const handleTouchStart = (e) => {
    touchStartX = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX = e.touches[0].clientX;
  };

  const handleTouchEnd = (stockSymbol) => {
    if (touchStartX - touchEndX > 50) {
      setSwipedRow && setSwipedRow(stockSymbol);
    } else if (touchEndX - touchStartX > 50) {
      setSwipedRow && setSwipedRow('');
    }
  };

  const onRowSelect = () => {
    if (!isMobile && stock.symbol !== swipedRow) {
      navigate(`${UI_ROUTES.symbol}/${stock.symbol}`);
    }
  };

  const onRemoveHandler = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setRemoveSymbol(stock.symbol);
  };

  return (
    <React.Fragment key={stock.symbol}>
      {isMobile ? (
        <tr
          className={classNames(styles.row, {
            [styles.swipedLeft]: hasSwipeableRows && swipedRow === stock.symbol,
          })}
          onClick={() => navigate(`${UI_ROUTES.symbol}/${stock.symbol}`)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={() => handleTouchEnd(stock.symbol)}
        >
          <td className={classNames(styles.columnSymbolName, styles.columnSymbolNameWidthMobile)}>
            <div className={styles.wrapperContent}>
              <div className={styles.logo}>
                <CompanyLogo url={url || ''} logo={logo || ''} className={styles.logo} symbol={stock.symbol} />
              </div>
              <div className={styles.companyInfo}>
                <span className={styles.symbol}>{stock.symbol}</span>
                <span className={styles.company}>{renderName(displayName)}</span>
              </div>
            </div>
          </td>
          <td className={styles.columnChartMobile}>
            <ChartLine symbol={stock.symbol} />
          </td>
          <td className={styles.columnLastPriceMobile}>
            <div
              className={classNames({
                [styles.noPriceChange]: priceMovement === 'equal',
                [styles.negativeChange]: priceMovement === 'down',
                [styles.positiveChange]: priceMovement === 'up',
              })}
              style={{
                display: 'inline-block',
                width: 'fit-content',
              }}
            >
              {formatCurrency(roundToCurrencyFormat(currentPrice || 0))}
            </div>
            <div className={styles.columnPercentChange}>
              {changePercentWithSign ? (
                <span className={direction === '-' ? styles.priceTextMinus : styles.priceTextPlus}>{changePercentWithSign}</span>
              ) : (
                INVALID_VALUE
              )}
            </div>
          </td>
          {swipedRow === stock.symbol && hasSwipeableRows && (
            <td className={styles.removeButtonCell}>
              <ClickableImage
                src={removeIcon}
                className={styles.removeButton}
                onClick={onRemoveHandler}
              />
            </td>
          )}
        </tr>
      ) : (
        <tr className={styles.row} onClick={onRowSelect}>
          <td className={styles.columnSymbolName}>
            <div className={styles.wrapperContent}>
              <CompanyLogo url={url || ''} logo={logo || ''} className={styles.logo} symbol={stock.symbol} />
              <div className={styles.companyInfo}>
                <span className={styles.symbol}>{stock.symbol}</span>
                <span className={styles.company}>{getLimitedText(displayName, NAME_LENGTH_LIMIT)}</span>
              </div>
            </div>
          </td>
          <td className={styles.columnChart}>
            <ChartLine symbol={stock.symbol} />
          </td>
          <td className={styles.columnLastPrice}>
            <span
              className={classNames(styles.currentPrice, {
                [styles.noPriceChange]: priceMovement === 'equal',
                [styles.negativeChange]: priceMovement === 'down',
                [styles.positiveChange]: priceMovement === 'up',
              })}
            >
              {formatCurrency(roundToCurrencyFormat(currentPrice || 0))}
            </span>
          </td>
          <td className={styles.columnChange}>
            {change ? (
              <span className={direction === '-' ? styles.priceTextMinus : styles.priceTextPlus}>
                {direction === '+' ? `+${formatCurrency(change)}` : formatCurrency(change)}
              </span>
            ) : (
              '-'
            )}
          </td>
          <td className={styles.columnPercentChange}>
            <div className={styles.percentageAndButtonWrapper}>
              {
                changePercentWithSign
                  ? (
                    <span className={direction === '-'
                      ? styles.priceTextMinus
                      : styles.priceTextPlus}
                    >
                      {changePercentWithSign}
                    </span>
                  )
                  : INVALID_VALUE
              }
              {hasSwipeableRows && (
                <ClickableImage
                  src={removeIcon}
                  className={styles.removeButton}
                  onClick={onRemoveHandler}
                />
              )}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

export default WatchListRow;
